import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  IconButton,
  Stack,
  CssBaseline,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Typography,
  Grid,
  Button,
  useTheme,
  TextField,
  FormControl,
  FormLabel,
  Radio,
  RadioGroup,
  FormControlLabel,
  Popper,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import PropTypes from "prop-types";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import LocalMallOutlinedIcon from "@mui/icons-material/LocalMallOutlined";
import CurrencyExchangeOutlinedIcon from "@mui/icons-material/CurrencyExchangeOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";

import Topbar from "../../../../components/topbar/TopBar";
import Header from "../../../../components/header/Header";
import {
  decodeAdminToken,
  getToken,
  getTokenRefresh,
} from "../../../../helpers/token/refreshToken";
import SnackBar from "../../../../components/snackbar/SnackBar";
import { tokens } from "../../../../theme";
import { modalStyle } from "./DriverStyle";
import {
  createDriverPayment,
  getDriverByCompany,
  getDriverCommentsByDriverId,
  getDriverCompany,
  getDriverCounterById,
  getDriverEarningById,
  getDrivers,
  updateDriverComment,
} from "../../../../api/users/driver/driver";
import { DataGrid, esES } from "@mui/x-data-grid";
//sidebar
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme as themeStyles } from "@mui/material/styles";
import Sidebar from "../../../../components/sidebar/Sidebar";
import {
  StatusEnum,
  StatusEnumName,
} from "../../../../helpers/constants/Status";
import { UsersEnum } from "../../../../helpers/constants/Users";
import { UserStatus } from "../../../../module_firebase/domain/usecases/status/model/UserStatusModel";
import { UpdateUserStateUseCase } from "../../../../module_firebase/domain/usecases/status/UserStatusUseCase";
import { DeleteDriverUseCase } from "./domain/DeleteDriverUseCase";
import { filterDriversUseCase } from "./domain/filterDriversUseCase";
import { DriverMethodCollectingModal } from "./components/DriverMethodCollectingModal";
import { LastDriverPayment } from "./components/LastDriverPayment";

const driverFilterOptions = [
  { value: "fullname", label: "Nombre" },
  { value: "plates", label: "Placa" },
  { value: "dni", label: "DNI" },
  { value: "email", label: "Email" },
];

function formatPaymentPhone(phone) {
  return String(phone ?? "")
    .trim()
    .replace(/^\+51\s*/, "")
    .replace(/\s+/g, "")
    .replace(/(\d{3})(?=\d)/g, "$1 ");
}

function isOverflown(element) {
  return (
    element.scrollHeight > element.clientHeight ||
    element.scrollWidth > element.clientWidth
  );
}

const GridCellExpand = React.memo(function GridCellExpand(props) {
  const { width, value } = props;
  const wrapper = React.useRef(null);
  const cellDiv = React.useRef(null);
  const cellValue = React.useRef(null);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [showFullCell, setShowFullCell] = React.useState(false);
  const [showPopper, setShowPopper] = React.useState(false);

  const handleMouseEnter = () => {
    const isCurrentlyOverflown = isOverflown(cellValue.current);
    setShowPopper(isCurrentlyOverflown);
    setAnchorEl(cellDiv.current);
    setShowFullCell(true);
  };

  const handleMouseLeave = () => {
    setShowFullCell(false);
  };

  React.useEffect(() => {
    if (!showFullCell) {
      return undefined;
    }

    function handleKeyDown(nativeEvent) {
      // IE11, Edge (prior to using Bink?) use 'Esc'
      if (nativeEvent.key === "Escape" || nativeEvent.key === "Esc") {
        setShowFullCell(false);
      }
    }

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [setShowFullCell, showFullCell]);

  return (
    <Box
      ref={wrapper}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        alignItems: "center",
        lineHeight: "24px",
        width: 1,
        height: 1,
        position: "relative",
        display: "flex",
      }}
    >
      <Box
        ref={cellDiv}
        sx={{
          height: 1,
          width,
          display: "block",
          position: "absolute",
          top: 0,
        }}
      />
      <Box
        ref={cellValue}
        sx={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {value}
      </Box>
      {showPopper && (
        <Popper
          open={showFullCell && anchorEl !== null}
          anchorEl={anchorEl}
          style={{ width, marginLeft: -17 }}
        >
          <Paper
            elevation={1}
            style={{ minHeight: wrapper.current.offsetHeight - 3 }}
          >
            <Typography variant="body2" style={{ padding: 8 }}>
              {value}
            </Typography>
          </Paper>
        </Popper>
      )}
    </Box>
  );
});

const ListDrivers = () => {
  const themeSidebar = themeStyles();
  const matchesSM = useMediaQuery(themeSidebar.breakpoints.down("sm"));
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [drivers, setDrivers] = useState([]);
  const [methodCollectingDriver, setMethodCollectingDriver] = useState(null);
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [filterField, setFilterField] = useState("fullname");
  const [filterTerm, setFilterTerm] = useState("");
  const [appliedFilter, setAppliedFilter] = useState(null);
  const visibleDrivers = useMemo(
    () =>
      filterDriversUseCase(drivers, appliedFilter?.field, appliedFilter?.term),
    [drivers, appliedFilter],
  );

  const handleOpenFilterModal = () => {
    setFilterField(appliedFilter?.field ?? "fullname");
    setFilterTerm(appliedFilter?.term ?? "");
    setOpenFilterModal(true);
  };

  const handleApplyFilter = (event) => {
    event.preventDefault();
    setAppliedFilter({ field: filterField, term: filterTerm.trim() });
    setOpenFilterModal(false);
  };
  const [UID, setUID] = useState("");
  const [STATUS, setSTATUS] = useState("");
  const [FULLNAME, setFULLNAME] = useState("");
  const [PHONE, setPHONE] = useState("");
  const [DEBT, setDEBT] = useState(0);
  const [notes, setNotes] = useState("");
  const [pay, setPay] = useState(0);
  const [openRatingsModal, setOpenRatingsModal] = useState(false);
  const [selectedDriverRatings, setSelectedDriverRatings] = useState([]);
  const [selectedDriverName, setSelectedDriverName] = useState("");
  const [ratingsSearchTerm, setRatingsSearchTerm] = useState("");
  const [isLoadingRatings, setIsLoadingRatings] = useState(false);
  const [isUpdatingRatings, setIsUpdatingRatings] = useState(false);
  const [driverCounter, setDriverCounter] = useState(null);
  const [driverEarning, setDriverEarning] = useState(null);
  const [openChatModal, setOpenChatModal] = useState(false);

  const getPassengerName = (rating) => rating?.name ?? "Sin nombre";
  const formatRatingAmount = (rating) =>
    rating === 0 || rating ? Number(rating).toFixed(1) : "";
  const formatEarningAmount = (amount) =>
    amount === 0 || amount ? Number(amount).toFixed(2) : "0.00";
  const ratingFieldSx = {
    "& .MuiInputBase-root": {
      minHeight: 50,
      alignItems: "flex-start",
    },
  };
  const normalizeRatingSearchValue = (value) =>
    `${value ?? ""}`.trim().toLowerCase();
  const getRatingSortValue = (rating) => {
    const sortValue = Number(rating.ratingSortValue ?? rating.rating);
    return Number.isNaN(sortValue) ? Number.MAX_VALUE : sortValue;
  };

  const visibleDriverRatings = selectedDriverRatings
    .map((rating, index) => ({ ...rating, originalIndex: index }))
    .filter((rating) => {
      const searchValue = normalizeRatingSearchValue(ratingsSearchTerm);
      if (!searchValue) return true;

      return [
        rating.order_number,
        getPassengerName(rating),
        rating.rating,
        rating.comments,
      ]
        .map(normalizeRatingSearchValue)
        .some((fieldValue) => fieldValue.includes(searchValue));
    })
    .sort((firstRating, secondRating) => {
      return getRatingSortValue(firstRating) - getRatingSortValue(secondRating);
    });

  const handleRatingFieldChange = (index, field, value) => {
    setSelectedDriverRatings((currentRatings) =>
      currentRatings.map((rating, currentIndex) =>
        currentIndex === index ? { ...rating, [field]: value } : rating,
      ),
    );
  };

  const handleRatingUpdateSuccess = (index, formattedRating) => {
    const ratingValue = formattedRating.rating.toFixed(1);
    setSelectedDriverRatings((currentRatings) =>
      currentRatings.map((rating, currentIndex) =>
        currentIndex === index
          ? {
              ...rating,
              rating: ratingValue,
              ratingSortValue: ratingValue,
              comments: formattedRating.comments ?? "",
            }
          : rating,
      ),
    );
  };

  const validateRating = (rating) => {
    const numericRating = Number(rating.rating);
    if (
      rating.rating === "" ||
      Number.isNaN(numericRating) ||
      numericRating < 0 ||
      numericRating > 5
    ) {
      snackBarDialog("LA CALIFICACION DEBE ESTAR ENTRE 0.0 Y 5.0", "error");
      return false;
    }

    if ((rating.comments ?? "").length > 150) {
      snackBarDialog("EL COMENTARIO NO DEBE SUPERAR 150 CARACTERES", "error");
      return false;
    }

    return true;
  };

  const handleUpdateRating = async (index) => {
    const currentRating = selectedDriverRatings[index];
    if (!currentRating) return;
    if (!validateRating(currentRating)) return;

    setIsUpdatingRatings(true);

    try {
      const token = getTokenRefresh();
      const formattedRating = {
        ...currentRating,
        rating: Number(Number(currentRating.rating).toFixed(1)),
        comments: currentRating.comments ?? null,
      };

      await updateDriverComment(token, {
        id: formattedRating.id,
        drivers_id: formattedRating.drivers_id,
        name: formattedRating.name,
        rating: formattedRating.rating,
        comments: formattedRating.comments,
      });

      handleRatingUpdateSuccess(index, formattedRating);
      snackBarDialog("CALIFICACION ACTUALIZADA");
    } catch (error) {
      console.log(error);
      snackBarDialog("ERROR: NO SE LOGRO ACTUALIZAR LA CALIFICACION", "error");
    } finally {
      setIsUpdatingRatings(false);
    }
  };

  const handleOpenRatingsModal = async (driver) => {
    setSelectedDriverName(driver?.fullname ?? "");
    setSelectedDriverRatings([]);
    setRatingsSearchTerm("");
    setDriverCounter(null);
    setDriverEarning(null);
    setOpenRatingsModal(true);
    setIsLoadingRatings(true);

    try {
      const token = getTokenRefresh();
      const [response, counterResponse, earningResponse] = await Promise.all([
        getDriverCommentsByDriverId(token, driver.id),
        getDriverCounterById(token, driver.id),
        getDriverEarningById(token, driver.id),
      ]);
      const data = Array.isArray(response?.data?.data)
        ? response.data.data.map((rating) => ({
            ...rating,
            rating:
              rating?.rating === 0 || rating?.rating
                ? Number(rating.rating).toFixed(1)
                : "",
            ratingSortValue:
              rating?.rating === 0 || rating?.rating
                ? Number(rating.rating).toFixed(1)
                : "",
            comments: rating?.comments ?? "",
          }))
        : [];
      setSelectedDriverRatings(data);
      setDriverCounter(counterResponse?.data ?? null);
      setDriverEarning(earningResponse?.data ?? null);
    } catch (error) {
      console.log(error);
      snackBarDialog("ERROR: NO SE LOGRO CARGAR LAS CALIFICACIONES", "error");
    } finally {
      setIsLoadingRatings(false);
    }
  };

  useEffect(() => {
    const listDrivers = async () => {
      try {
        const token = getTokenRefresh();
        const admToken = getToken("adttkn");
        const { company_id } = decodeAdminToken(admToken);
        if (token) {
          // const response = await getDrivers(token);
          // const response = await getDriverByCompany(token, company_id);
          const response = await getDriverCompany(token, company_id);
          setDrivers([]);
          console.log(response);

          let count = 0;
          Object.values(response.data).map((driver) => {
            if (driver.status !== 3 && driver.approved) {
              let plates = "";
              let bodyworks = "";
              Object.values(driver?.vehicle ?? {}).map((v) => {
                plates += (v?.vehicle_plate ?? "") + ", ";
                bodyworks += (v?.vehicle_bodywork ?? "") + ", ";
              });
              driver.plates = plates;
              driver.bodyworks = bodyworks;
              driver.index = ++count;
              driver.driverStatus = StatusEnumName(driver.status);
              setDrivers((olData) => [...olData, driver]);
            }
          });
        }
      } catch (error) {
        console.log(error);
      }
    };
    listDrivers();
  }, []);

  GridCellExpand.propTypes = {
    value: PropTypes.string.isRequired,
    width: PropTypes.number.isRequired,
  };

  function renderCellExpand(params) {
    return (
      <GridCellExpand
        value={params.value || ""}
        width={params.colDef.computedWidth}
      />
    );
  }

  renderCellExpand.propTypes = {
    colDef: PropTypes.object.isRequired,
    value: PropTypes.string.isRequired,
  };

  // const url = `https://api.whatsapp.com/send?=&text=*Conductor:* ${props?.fullname}%0A*DNI:* ${props?.dni}%0A*Placa:* ${props?.vehicle[0]?.vehicle_plate ?? ""}%0A*Carroceria:* ${props?.vehicle[0]?.vehicle_bodywork ?? ""}%0A*Telefono:* ${props?.phone}`;

  const columns = [
    { headerName: "ID", field: "index", width: 50 },
    {
      headerName: "NOMBRE",
      field: "fullname",
      type: "string",
      headerAlign: "left",
      align: "left",
      width: 200,
      renderCell: ({ row: { ...props } }) => {
        const url = `https://api.whatsapp.com/send?=&text=*Conductor:* ${props?.fullname}%0A*DNI:* ${props?.dni}%0A*Placa:* ${props?.plates}%0A*Carroceria:* ${props?.bodyworks}%0A*Telefono:* ${props?.phone}`;
        return (
          <a
            href={url}
            target="_blank"
            style={{
              color: "white",
              fontSize: 14,
              listStyle: "none",
              textDecoration: "none",
            }}
          >
            {props?.fullname}
          </a>
        );
      },
    },
    {
      headerName: "EMAIL",
      field: "email",
      width: 180,
    },
    {
      headerName: "TELEFONO",
      field: "phone",
      width: 100,
    },
    {
      headerName: "DIRECCION",
      field: "address",
      width: 100,
      renderCell: renderCellExpand,
    },
    {
      headerName: "DNI",
      field: "dni",
      width: 80,
    },
    {
      headerName: "PLACA",
      field: "plates",
      width: 80,
      renderCell: renderCellExpand,
    },
    {
      headerName: "CARROCERIA",
      field: "bodyworks",
      width: 80,
      renderCell: renderCellExpand,
    },
    {
      headerName: "EDAD",
      field: "date_of_birth",
      width: 50,
    },
    {
      headerName: "ESTADO",
      field: "driverStatus",
      width: 90,
    },
    {
      headerName: "Rating",
      headerAlign: "center",
      field: "rating",
      width: 60,
      align: "center",
      renderCell: ({ row }) => {
        return (
          <div
            style={{
              fontSize: 18,
            }}
            onClick={() => handleOpenRatingsModal(row)}
          >
            {formatRatingAmount(row.rating)}
          </div>
        );
      },
    },
    {
      headerName: "RESERVAS",
      field: "bookings",
      width: 80,
      headerAlign: "center",
      align: "center",
      renderCell: ({ row: { bookings } }) => {
        return (
          <div
            style={{
              color: bookings == 1 ? "white" : "red",
              fontSize: 18,
              width: "100%",
              textAlign: "center",
            }}
          >
            {bookings == 1 ? "SI" : "NO"}
          </div>
        );
      },
    },
    {
      headerName: "DEUDA",
      field: "total_amount",
      width: 90,
      renderCell: ({ row: { total_amount } }) => {
        return (
          <div
            style={{
              color: total_amount >= 0 ? "white" : "red",
              fontSize: 18,
              width: "100%",
              textAlign: "right",
            }}
          >
            {(Math.round(total_amount * 100) / 100).toFixed(2)}
          </div>
        );
      },
    },
    {
      headerName: "M.Cobro",
      field: "methodCollecting",
      width: 100,
      sortable: false,
      filterable: false,
      headerAlign: "center",
      align: "center",
      renderCell: ({ row }) => (
        <Button
          size="small"
          variant="contained"
          color="secondary"
          onClick={(event) => {
            event.stopPropagation();
            setMethodCollectingDriver(row);
          }}
        >
          Ver
        </Button>
      ),
    },
    {
      headerName: "Actions",
      field: "actions",
      width: 260,
      renderCell: ({ row: { id, fullname, phone, total_amount, status } }) => {
        return (
          <Stack direction="row" spacing={0.5}>
            {/* HISTORIAL */}
            <IconButton
              color="success"
              aria-label="add"
              component="label"
              sx={{ background: "#ffff" }}
              onClick={() => {
                navigate(`/admin/driver-history/${id}`, {
                  state: { fullname },
                });
              }}
            >
              <HistoryOutlinedIcon />
            </IconButton>
            {/* HISTORIAL DE PAGOS */}
            <IconButton
              color="success"
              aria-label="add"
              component="label"
              sx={{ background: "#ffff" }}
              onClick={() => {
                navigate(`/admin/driver-history-fixed/${id}`, {
                  state: { fullname },
                });
              }}
            >
              <LocalMallOutlinedIcon />
            </IconButton>

            {/* COBRAR/PAGAR */}
            <IconButton
              color="success"
              aria-label="add"
              component="label"
              sx={{ background: "#ffff" }}
              onClick={() => {
                setUID(id);
                setSTATUS(status);
                setFULLNAME(fullname);
                setPHONE(phone ?? "");
                setDEBT(total_amount);
                setOpenModalDebt(true);
              }}
            >
              <CurrencyExchangeOutlinedIcon />
            </IconButton>

            {/* EDITAR */}
            <IconButton
              color="success"
              aria-label="add"
              component="label"
              sx={{ background: "#ffff" }}
              onClick={() => {
                navigate(`/admin/driver/${id}`, {
                  state: { fullname },
                });
              }}
            >
              <BorderColorOutlinedIcon />
            </IconButton>
            {/* ELIMINAR */}
            <IconButton
              aria-label="delete"
              component="label"
              sx={{ background: "#AA151B" }}
              onClick={() => {
                setUID(id);
                setSTATUS(status);
                setOpen(true);
              }}
            >
              <DeleteIcon />
            </IconButton>
            {/* EDITAR */}
            <IconButton
              color="success"
              aria-label="add"
              component="label"
              sx={{ background: "#ffff" }}
              onClick={() => {
                navigate(`/admin/driver-rate/${id}`, {
                  state: { fullname },
                });
              }}
            >
              <BorderColorOutlinedIcon />
            </IconButton>
          </Stack>
        );
      },
    },
  ];

  const [open, setOpen] = useState(false);
  const handleModalClose = () => setOpen(false);
  const [openModalDebt, setOpenModalDebt] = useState(false);
  const handleModalDebtClose = () => setOpenModalDebt(false);
  const handleRatingsModalClose = () => {
    setOpenRatingsModal(false);
    setRatingsSearchTerm("");
  };
  const handleChatModalClose = () => setOpenChatModal(false);

  const [isLoading, setIsLoading] = useState(false);
  const [messageSnk, setMessageSnk] = useState("");
  const [stateSnk, setStateSnk] = useState(false);
  const [severity, setSeverity] = useState("success");
  const vertical = "top";
  const horizontal = "center";
  const handleSnkClose = () => setStateSnk(false);

  const snackBarDialog = (message, severity = "success") => {
    setMessageSnk(message);
    setSeverity(severity);
    setStateSnk(true);
  };

  const updateStatusDriver = async (status) => {
    setIsLoading(true);
    try {
      const resp = await DeleteDriverUseCase(UID, status);
      if (resp.message === "SUCCESS") {
        const userStatus = new UserStatus(UID, UsersEnum.DRIVER, status);
        await UpdateUserStateUseCase(userStatus);

        switch (status) {
          case 1 && 2:
            drivers.map((driver) => {
              if (driver.id === UID) {
                driver.status = status;
                driver.driverStatus = StatusEnumName(status);
              }
            });
            snackBarDialog("ESTADO ACTUALIZADO");
            break;
          case 3:
            setDrivers(drivers.filter((driver) => driver.id !== UID));
            snackBarDialog("CONDUCTOR ELIMINADO");
            break;
        }
      } else snackBarDialog("ERROR: NO SE LOGRO REALIZAR EL CAMBIO");

      setIsLoading(false);
      setOpen(false);
    } catch (error) {
      snackBarDialog("ERROR: NO SE LOGRO REALIZAR EL CAMBIO");
      console.log(error);
      setIsLoading(false);
    }
  };

  const updateDriverPayment = async (payment, notes) => {
    try {
      if (value) {
        setIsLoading(true);
        console.log(UID, payment, notes);
        const token = "";
        if (value === "cobrar") {
          await createDriverPayment(token, UID, -payment, notes.toUpperCase());
        } else {
          await createDriverPayment(token, UID, payment, notes.toUpperCase());
        }
        snackBarDialog("ESTADO ACTUALIZADO");
        drivers.map((driver) => {
          if (driver.id === UID) {
            if (value === "cobrar") {
              driver.total_amount =
                Math.round((Number(DEBT) + Number(pay)) * 10) / 10;
            } else {
              driver.total_amount =
                Math.round((Number(DEBT) - Number(pay)) * 10) / 10;
            }
          }
        });
        setDEBT(0);
        setPay(0);
        setFULLNAME("");
        setIsLoading(false);
        setOpenModalDebt(false);
      } else {
        snackBarDialog("SELECCIONE OPERACION", "error");
      }
    } catch (error) {
      snackBarDialog("ERROR NO SE GUARDO EL COBRO/PAGO", "error");
      console.log(error);
      setIsLoading(false);
    }
  };

  const [value, setValue] = React.useState(null);

  const handleChange = (event) => {
    setValue(event.target.value);
  };

  return (
    <>
      <CssBaseline />
      <div className="app">
        {!matchesSM && <Sidebar />}
        <main className="content">
          <Topbar />
          <Box m="10px">
            <Box display="flex" justifyContent="space-between">
              <Header title="LISTA DE CONDUCTORES" subtitle="" />

              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                flexWrap="wrap"
                sx={{ mb: 1 }}
              >
                <Button
                  variant="contained"
                  startIcon={<FilterListOutlinedIcon />}
                  onClick={handleOpenFilterModal}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 700,
                    bgcolor: "#165c53",
                    color: "#fff",
                    "&:hover": { bgcolor: "#12483f" },
                  }}
                >
                  Filtro
                </Button>
                {appliedFilter && (
                  <>
                    <Typography>
                      {
                        driverFilterOptions.find(
                          (option) => option.value === appliedFilter.field,
                        )?.label
                      }
                      : {appliedFilter.term}
                      {" · "}
                      {visibleDrivers.length} resultados
                    </Typography>
                    <Button
                      color="secondary"
                      onClick={() => setAppliedFilter(null)}
                    >
                      Limpiar filtro
                    </Button>
                  </>
                )}
              </Stack>
            </Box>
            <Box display="grid" gap="10px">
              <Box gridColumn="span 12" gridRow="span 2">
                <Box
                  m="10px 0 0 0"
                  height="100vh"
                  sx={{
                    "& .MuiDataGrid-root": {
                      border: "none",
                    },
                    "& .MuiDataGrid-cell": {
                      borderBottom: "none",
                    },
                    "& .name-column--cell": {
                      color: colors.greenAccent[300],
                    },
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: colors.blueAccent[700],
                      borderBottom: "none",
                    },
                    "& .MuiDataGrid-virtualScroller": {
                      backgroundColor: colors.primary[400],
                    },
                    "& .MuiDataGrid-footerContainer": {
                      borderTop: "none",
                      backgroundColor: colors.blueAccent[700],
                    },
                    "& .MuiCheckbox-root": {
                      color: `${colors.greenAccent[200]} !important`,
                    },
                  }}
                >
                  <DataGrid
                    getRowId={(row) => row.index}
                    rows={visibleDrivers}
                    columns={columns}
                    localeText={
                      esES.components.MuiDataGrid.defaultProps.localeText
                    }
                  />
                </Box>
              </Box>
            </Box>
            {methodCollectingDriver && (
              <DriverMethodCollectingModal
                driver={methodCollectingDriver}
                onClose={() => setMethodCollectingDriver(null)}
              />
            )}
            <Dialog
              open={openFilterModal}
              onClose={() => setOpenFilterModal(false)}
              fullWidth
              maxWidth="xs"
              aria-labelledby="driver-filter-title"
              PaperProps={{ sx: { borderRadius: 3 } }}
            >
              <Box component="form" onSubmit={handleApplyFilter}>
                <DialogTitle
                  id="driver-filter-title"
                  sx={{ bgcolor: "#165c53", color: "#fff", fontWeight: 700 }}
                >
                  Filtrar conductores
                </DialogTitle>
                <DialogContent>
                  <Stack spacing={2} sx={{ pt: 3 }}>
                    <TextField
                      select
                      fullWidth
                      label="Filtrar por"
                      value={filterField}
                      onChange={(event) => setFilterField(event.target.value)}
                    >
                      {driverFilterOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      autoFocus
                      fullWidth
                      label={
                        driverFilterOptions.find(
                          (option) => option.value === filterField,
                        )?.label
                      }
                      value={filterTerm}
                      onChange={(event) => setFilterTerm(event.target.value)}
                    />
                  </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                  <Button
                    color="secondary"
                    onClick={() => setOpenFilterModal(false)}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={!filterTerm.trim()}
                    startIcon={<FilterListOutlinedIcon />}
                    sx={{
                      borderRadius: 2,
                      fontWeight: 700,
                      bgcolor: "#165c53",
                      color: "#fff",
                      "&:hover": { bgcolor: "#12483f" },
                    }}
                  >
                    Filtrar
                  </Button>
                </DialogActions>
              </Box>
            </Dialog>
            {/* DELETE */}
            <Modal open={open} onClose={handleModalClose}>
              <Box backgroundColor={colors.primary[400]} sx={modalStyle}>
                <Typography variant="h6" component="h2" textAlign={"center"}>
                  ELIMINAR CONDUCTOR?
                </Typography>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={4} md={4} textAlign="center">
                    {STATUS === 1 ? (
                      <Button
                        color="secondary"
                        variant="contained"
                        onClick={() =>
                          updateStatusDriver(StatusEnum.SUSPENDIDO)
                        }
                        disabled={isLoading}
                      >
                        SUSPENDER
                      </Button>
                    ) : (
                      <Button
                        color="secondary"
                        variant="contained"
                        onClick={() => updateStatusDriver(StatusEnum.ACTIVO)}
                        disabled={isLoading}
                      >
                        HABILITAR
                      </Button>
                    )}
                  </Grid>
                  <Grid item xs={4} md={4} textAlign="center">
                    <Button
                      color="secondary"
                      variant="contained"
                      onClick={() => updateStatusDriver(StatusEnum.ELIMINADO)}
                      disabled={isLoading}
                    >
                      ELIMINAR
                    </Button>
                  </Grid>
                  <Grid item xs={4} md={4} textAlign="center">
                    <Button
                      color="error"
                      variant="contained"
                      onClick={handleModalClose}
                      disabled={isLoading}
                    >
                      CANCELAR
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Modal>

            {/* modal de pagos */}
            <Modal
              open={openModalDebt}
              onClose={handleModalDebtClose}
              aria-labelledby="modal-debt-title"
            >
              <Box
                sx={{
                  ...modalStyle,
                  width: { xs: "calc(100% - 32px)", sm: 420 },
                  boxSizing: "border-box",
                  maxHeight: "90vh",
                  overflowY: "auto",
                  p: { xs: 1, sm: 1 },
                  border: "1px solid rgba(16, 24, 40, 0.08)",
                  borderRadius: 3,
                  backgroundColor: "#fff",
                  color: "#101828",
                  "& .MuiInputBase-root": { color: "#101828", borderRadius: 2 },
                  "& .MuiInputLabel-root": { color: "#475467" },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#d0d5dd",
                  },
                }}
              >
                <Grid container spacing={1}>
                  <Grid item xs={12} textAlign={"center"}>
                    <Box
                      sx={{
                        p: 0.5,
                        borderRadius: 2,
                        background:
                          "linear-gradient(135deg, #0f3d3e, #165c53, #2d8b74)",
                        color: "#fff",
                      }}
                    >
                      <Typography
                        id="modal-debt-title"
                        component="h2"
                        variant="h4"
                        sx={{ fontWeight: 800 }}
                      >
                        PAGOS
                      </Typography>
                      <Typography
                        id="modal-phone-driver"
                        sx={{
                          fontSize: 28,
                          mt: 0.5,
                          color: "#fff",
                          fontWeight: "bold",
                        }}
                      >
                        {formatPaymentPhone(PHONE)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#667085",
                        fontWeight: 700,
                        letterSpacing: 0.6,
                      }}
                    >
                      CONDUCTOR
                    </Typography>
                    <Typography
                      sx={(theme) => ({
                        fontSize: `calc(${theme.typography.body1.fontSize} + 4px)`,
                        fontWeight: 700,
                        overflowWrap: "anywhere",
                      })}
                    >
                      {FULLNAME}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 2,
                        border: "1px solid",
                        alignItems: "center",
                        textAlign: "center",
                        borderColor:
                          DEBT < 0
                            ? "#fecdca"
                            : DEBT > 0
                              ? "#abefc6"
                              : "#e4e7ec",
                        bgcolor:
                          DEBT < 0
                            ? "#fef3f2"
                            : DEBT > 0
                              ? "#ecfdf3"
                              : "#f9fafb",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 28,
                          fontWeight: 800,
                          lineHeight: 1.2,
                          fontVariantNumeric: "tabular-nums",
                          color:
                            DEBT < 0
                              ? "#b42318"
                              : DEBT > 0
                                ? "#067647"
                                : "#344054",
                        }}
                      >
                        {DEBT < 0 ? "Cobrar " : DEBT > 0 ? "Pagar +" : ""}
                        {DEBT.toFixed(2)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      value={notes}
                      fullWidth
                      label="NOTAS"
                      variant="outlined"
                      name="notes"
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <FormLabel
                        id="radio-buttons"
                        sx={{
                          color: "#475467",
                          fontWeight: 700,
                          mb: 0.5,
                          "&.Mui-focused": { color: "#475467" },
                        }}
                      >
                        OPERACION
                      </FormLabel>
                      <RadioGroup
                        row
                        aria-labelledby="radio-buttons"
                        name="radio-buttons"
                        value={value}
                        onChange={handleChange}
                        sx={{ gap: 1, flexWrap: "nowrap", mb: 0.5 }}
                      >
                        <FormControlLabel
                          value="pagar"
                          control={
                            <Radio
                              sx={{
                                color: "#667085",
                                "&.Mui-checked": { color: "#fff" },
                              }}
                            />
                          }
                          label="PAGAR"
                          sx={{
                            m: 0,
                            flex: 1,
                            p: 0.5,
                            borderRadius: 2,
                            border: "2px solid",
                            borderColor:
                              value === "pagar" ? "#165c53" : "#e4e7ec",
                            bgcolor: value === "pagar" ? "#165c53" : "#f9fafb",
                            color: value === "pagar" ? "#fff" : "#475467",
                            "& .MuiFormControlLabel-label": { fontWeight: 700 },
                          }}
                        />
                        <FormControlLabel
                          value="cobrar"
                          control={
                            <Radio
                              sx={{
                                color: "#667085",
                                "&.Mui-checked": { color: "#fff" },
                              }}
                            />
                          }
                          label="COBRAR"
                          sx={{
                            m: 0,
                            flex: 1,
                            p: 0.5,
                            borderRadius: 2,
                            border: "2px solid",
                            borderColor:
                              value === "cobrar" ? "#165c53" : "#e4e7ec",
                            bgcolor: value === "cobrar" ? "#165c53" : "#f9fafb",
                            color: value === "cobrar" ? "#fff" : "#475467",
                            "& .MuiFormControlLabel-label": { fontWeight: 700 },
                          }}
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      value={pay}
                      fullWidth
                      type="number"
                      sx={{ "& .MuiInputBase-input": { fontSize: 26 } }}
                      label={
                        value === "pagar" ? "MONTO A PAGAR" : "MONTO A COBRAR"
                      }
                      variant="outlined"
                      name="pay"
                      onChange={(e) => {
                        setPay(e.target.value);
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    {openModalDebt && <LastDriverPayment key={UID} driverId={UID} />}
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      fullWidth
                      color="secondary"
                      variant="contained"
                      onClick={(e) => {
                        e.preventDefault();
                        console.log(UID, pay);
                        updateDriverPayment(pay, notes);
                      }}
                      disabled={isLoading}
                      sx={{
                        fontSize: 20,
                        minHeight: 46,
                        borderRadius: 2,
                        fontWeight: 700,
                        bgcolor: "#165c53",
                        color: "#fff",
                        boxShadow: "none",
                        "&:hover": { bgcolor: "#12483f", boxShadow: "none" },
                      }}
                    >
                      {value === "pagar" ? "pagar" : "cobrar"}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Modal>
            <Modal
              open={openRatingsModal}
              onClose={handleRatingsModalClose}
              aria-labelledby="modal-ratings-title"
            >
              <Box
                sx={{
                  ...modalStyle,
                  width: { xs: "90%", sm: 760 },
                  maxHeight: "80vh",
                  overflowY: "auto",
                  backgroundColor: colors.primary[400],
                }}
              >
                <Typography
                  id="modal-ratings-title"
                  variant="h5"
                  component="h2"
                  textAlign="center"
                  sx={{ fontWeight: 900 }}
                  mb={selectedDriverName ? 0.5 : 2}
                >
                  Lista de Calificaciones
                </Typography>
                {selectedDriverName && (
                  <Typography textAlign="center" mb={2}>
                    {selectedDriverName}
                  </Typography>
                )}
                {driverCounter && (
                  <Box
                    mb={2}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                      },
                      gap: 1.25,
                    }}
                  >
                    {[
                      {
                        title: "Servicios",
                        metrics: [
                          {
                            label: "Cliente",
                            value: driverCounter.countRider ?? 0,
                          },
                          {
                            label: "Empresa",
                            value: driverCounter.countEmployee ?? 0,
                          },
                        ],
                      },
                      {
                        title: "Ganancias",
                        metrics: [
                          {
                            label: "Cliente",
                            value: formatEarningAmount(
                              driverEarning?.total_client,
                            ),
                          },
                          {
                            label: "Empresa",
                            value: formatEarningAmount(
                              driverEarning?.total_enterprise,
                            ),
                          },
                        ],
                      },
                    ].map((section) => (
                      <Box
                        key={section.title}
                        sx={{
                          p: 1.25,
                          borderRadius: 1,
                          backgroundColor: colors.primary[500],
                          border: "1px solid rgba(255, 255, 255, 0.18)",
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            display: "block",
                            mb: 1,
                            fontWeight: 900,
                            textTransform: "uppercase",
                            color: colors.greenAccent[300],
                            letterSpacing: 0.4,
                            textAlign: "center",
                          }}
                        >
                          {section.title}
                        </Typography>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                            gap: 1,
                          }}
                        >
                          {section.metrics.map((metric) => (
                            <Box
                              key={`${section.title}-${metric.label}`}
                              sx={{
                                p: 1,
                                borderRadius: 1,
                                backgroundColor: colors.primary[400],
                                border: "1px solid rgba(255, 255, 255, 0.12)",
                                textAlign: "center",
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  display: "block",
                                  fontWeight: 800,
                                  textTransform: "uppercase",
                                  color: colors.grey[300],
                                }}
                              >
                                {metric.label}
                              </Typography>
                              <Typography
                                variant="h6"
                                sx={{
                                  mt: 0.25,
                                  fontWeight: 900,
                                  lineHeight: 1.15,
                                }}
                              >
                                {metric.value}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
                <Stack spacing={1.5}>
                  {isLoadingRatings ? (
                    <Box display="flex" justifyContent="center" py={2}>
                      <CircularProgress color="secondary" />
                    </Box>
                  ) : selectedDriverRatings.length > 0 ? (
                    <>
                      <TextField
                        value={ratingsSearchTerm}
                        fullWidth
                        label="Buscar"
                        variant="outlined"
                        size="small"
                        onChange={(event) =>
                          setRatingsSearchTerm(event.target.value)
                        }
                      />

                      <TableContainer
                        component={Paper}
                        elevation={0}
                        sx={{
                          borderRadius: 1,
                          border: "1px solid rgba(255, 255, 255, 0.18)",
                          overflowX: "auto",
                          backgroundColor: colors.primary[500],
                        }}
                      >
                        <Table
                          size="small"
                          aria-label="tabla de calificaciones"
                        >
                          <TableHead>
                            <TableRow
                              sx={{
                                backgroundColor: colors.primary[600],
                              }}
                            >
                              <TableCell sx={{ fontWeight: 40 }}>
                                PEDIDO
                              </TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>
                                CLIENTE
                              </TableCell>
                              <TableCell sx={{ fontWeight: 800, minWidth: 80 }}>
                                CALIFICACION
                              </TableCell>
                              <TableCell
                                sx={{ fontWeight: 800, minWidth: 230 }}
                              >
                                COMENTARIO
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 800 }}>
                                Acciones
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {visibleDriverRatings.length > 0 ? (
                              visibleDriverRatings.map((rating) => (
                                <TableRow
                                  key={`${getPassengerName(rating)}-${rating.originalIndex}`}
                                  sx={{
                                    "&:last-child td, &:last-child th": {
                                      borderBottom: 0,
                                    },
                                  }}
                                >
                                  <TableCell component="th" scope="row">
                                    {rating.order_number}
                                  </TableCell>
                                  <TableCell component="th" scope="row">
                                    {getPassengerName(rating)}
                                  </TableCell>
                                  <TableCell>
                                    <TextField
                                      value={rating.rating ?? ""}
                                      fullWidth
                                      type="number"
                                      label="Calificacion"
                                      variant="outlined"
                                      margin="dense"
                                      sx={ratingFieldSx}
                                      inputProps={{
                                        min: 0,
                                        max: 5,
                                        step: 0.1,
                                      }}
                                      disabled={isUpdatingRatings}
                                      onChange={(event) =>
                                        handleRatingFieldChange(
                                          rating.originalIndex,
                                          "rating",
                                          event.target.value,
                                        )
                                      }
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <TextField
                                      value={rating.comments ?? ""}
                                      fullWidth
                                      label="Comentario"
                                      variant="outlined"
                                      margin="dense"
                                      sx={ratingFieldSx}
                                      inputProps={{ maxLength: 150 }}
                                      disabled={isUpdatingRatings}
                                      onChange={(event) =>
                                        handleRatingFieldChange(
                                          rating.originalIndex,
                                          "comments",
                                          event.target.value,
                                        )
                                      }
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      justifyContent="flex-end"
                                    >
                                      <Button
                                        color="secondary"
                                        variant="contained"
                                        disabled={isUpdatingRatings}
                                        onClick={() =>
                                          handleUpdateRating(
                                            rating.originalIndex,
                                          )
                                        }
                                      >
                                        {isUpdatingRatings
                                          ? "ACTUALIZANDO..."
                                          : "ACTUALIZAR"}
                                      </Button>
                                      <IconButton
                                        color="success"
                                        aria-label="chat"
                                        sx={{ background: "#ffff" }}
                                        onClick={() => setOpenChatModal(true)}
                                      >
                                        <ChatOutlinedIcon />
                                      </IconButton>
                                    </Stack>
                                  </TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={4}>
                                  <Typography textAlign="center" py={2}>
                                    No hay calificaciones que coincidan con la
                                    busqueda.
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  ) : (
                    <Typography textAlign="center">
                      No hay calificaciones registradas.
                    </Typography>
                  )}
                </Stack>
              </Box>
            </Modal>
            <Modal
              open={openChatModal}
              onClose={handleChatModalClose}
              aria-labelledby="modal-chat-title"
            >
              <Box
                sx={{
                  ...modalStyle,
                  width: { xs: "90%", sm: 420 },
                  backgroundColor: colors.primary[400],
                }}
              >
                <Typography
                  id="modal-chat-title"
                  variant="h5"
                  component="h2"
                  textAlign="center"
                  sx={{ fontWeight: 900 }}
                >
                  Chat del Pedido
                </Typography>
              </Box>
            </Modal>
            <SnackBar
              stateSnk={stateSnk}
              vertical={vertical}
              horizontal={horizontal}
              duration={5000}
              handleClose={handleSnkClose}
              severity={severity}
              messageSnk={messageSnk}
            />
          </Box>
        </main>
      </div>
    </>
  );
};

export default ListDrivers;
